import { FC } from 'react';

import { keyframes } from '@emotion/react';
import { Box } from '@mui/material';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/TailwindComponents/base/Tooltip';
import { IFlag } from '@/models';

interface FlagChipsProps {
  flags: IFlag[];
  size?: 'small' | 'medium' | 'large';
  maxDisplay?: number;
  animateImportant?: boolean;
  flexWrap?:
    | 'wrap'
    | 'nowrap'
    | 'wrap-reverse'
    | 'inherit'
    | 'initial'
    | 'unset';
  className?: string;
}

const FlagChips: FC<FlagChipsProps> = ({
  flags = [],
  flexWrap = 'wrap',
  size = 'small',
  maxDisplay = 5,
  animateImportant = false,
  className = '',
}) => {
  if (!flags.length) {
    return null;
  }

  const displayFlags = flags.slice(0, maxDisplay);
  const remainingCount = flags.length - maxDisplay;

  const sizeStyles = {
    small: { width: 48, height: 24, fontSize: '0.75rem' },
    medium: { width: 64, height: 32, fontSize: '0.875rem' },
    large: { width: 80, height: 40, fontSize: '1rem' },
  };
  const pulseInsetBySize = {
    small: 6,
    medium: 7,
    large: 8,
  };
  const importantAnimationDuration = '1s';
  const importantPulse = keyframes`
    0% {
      transform: scale(0.92);
      opacity: 0.7;
    }
    55% {
      transform: scale(1.28);
      opacity: 0;
    }
    100% {
      transform: scale(1.28);
      opacity: 0;
    }
  `;
  const importantGrow = keyframes`
    0% {
      transform: scale(1);
    }
    55% {
      transform: scale(1.12);
    }
    100% {
      transform: scale(1);
    }
  `;

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        gap: 0.5,
        flexWrap: { flexWrap },
        alignItems: 'center',
      }}
    >
      {displayFlags.map((flag) => {
        const shouldAnimate = animateImportant && flag.isImportant;

        return (
          <TooltipProvider key={flag._id || flag.code}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    marginY: 0.5,
                    '&::before': shouldAnimate
                      ? {
                          content: '""',
                          position: 'absolute',
                          inset: `-${pulseInsetBySize[size]}px`,
                          borderRadius: 2,
                          backgroundColor: 'error.dark',
                          opacity: 0.55,
                          transformOrigin: 'center',
                          animation: `${importantPulse} ${importantAnimationDuration} linear infinite`,
                        }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      ...sizeStyles[size],
                      position: 'relative',
                      zIndex: 1,
                      backgroundColor: 'error.main',
                      color: 'white',
                      fontWeight: 'bold',
                      display: 'flex',
                      cursor: 'default',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      paddingX: 1,
                      transformOrigin: 'center',
                      animation: shouldAnimate
                        ? `${importantGrow} ${importantAnimationDuration} linear infinite`
                        : undefined,
                    }}
                    role="status"
                    aria-label={`Flag code ${flag.code}`}
                    data-important-animated={shouldAnimate ? 'true' : undefined}
                  >
                    {flag.code}
                  </Box>
                </Box>
              </TooltipTrigger>
              <TooltipPrimitive.Portal>
                <TooltipContent
                  side="top"
                  className="max-w-xs break-words"
                  style={{ zIndex: 10000 }}
                >
                  {flag.name}
                </TooltipContent>
              </TooltipPrimitive.Portal>
            </Tooltip>
          </TooltipProvider>
        );
      })}
      {remainingCount > 0 && (
        <Box
          sx={{
            ...sizeStyles[size],
            backgroundColor: 'error.main',
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            paddingX: 1,
            marginY: 0.5,
          }}
          title={`${remainingCount} more flags`}
          role="status"
          aria-label={`${remainingCount} additional flags`}
        >
          +{remainingCount}
        </Box>
      )}
    </Box>
  );
};

export default FlagChips;
